import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

interface NukeButtonDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

/**
 * Dramatic confirmation dialog for emergency stop (nuke button)
 * Features a large red button with pulsing animation and clear warnings
 */
export function NukeButtonDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: NukeButtonDialogProps) {
  const [isArmed, setIsArmed] = useState(false);

  // Reset armed state whenever dialog opens to ensure two-step confirmation
  // This handles the case where dialog is closed directly (e.g., after successful mutation)
  // without going through handleClose()
  useEffect(() => {
    if (isOpen) {
      setIsArmed(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsArmed(false);
    onClose();
  };

  const handleArmClick = () => {
    setIsArmed(true);
  };

  const handleNukeClick = () => {
    if (isArmed) {
      onConfirm();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleBackdropClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-gray-900 border border-red-900/50 rounded-2xl shadow-2xl max-w-md w-full p-8 relative overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Danger gradient background */}
            <div className="absolute inset-0 bg-gradient-to-b from-red-950/30 to-transparent pointer-events-none" />

            {/* Close button */}
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Content */}
            <div className="relative z-10 space-y-6">
              {/* Warning Header */}
              <div className="text-center space-y-2">
                <div className="flex justify-center">
                  <div className="p-3 bg-red-500/20 rounded-full">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white">Emergency Stop</h2>
                <p className="text-gray-400 text-sm">
                  This will immediately block ALL webhook trades
                </p>
              </div>

              {/* The Nuke Button */}
              <div className="flex justify-center py-4">
                {!isArmed ? (
                  <motion.button
                    onClick={handleArmClick}
                    disabled={isLoading}
                    className="relative w-40 h-40 rounded-full bg-gradient-to-b from-gray-700 to-gray-800 border-4 border-gray-600 shadow-lg hover:border-gray-500 transition-all disabled:opacity-50 group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Button surface */}
                    <div className="absolute inset-3 rounded-full bg-gradient-to-b from-red-600 to-red-800 flex items-center justify-center shadow-inner">
                      <span className="text-white font-bold text-lg tracking-wider">ARM</span>
                    </div>
                    {/* Outer ring glow on hover */}
                    <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-red-500/20 blur-md" />
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={handleNukeClick}
                    disabled={isLoading}
                    className="relative w-40 h-40 rounded-full bg-gradient-to-b from-gray-700 to-gray-800 border-4 border-red-500 shadow-lg disabled:opacity-50"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {/* Pulsing glow */}
                    <motion.div
                      className="absolute inset-0 rounded-full bg-red-500/30 blur-xl"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                    {/* Button surface - now bright red */}
                    <motion.div
                      className="absolute inset-3 rounded-full bg-gradient-to-b from-red-500 to-red-700 flex items-center justify-center shadow-inner"
                      animate={{ 
                        boxShadow: [
                          '0 0 20px rgba(239, 68, 68, 0.5)',
                          '0 0 40px rgba(239, 68, 68, 0.8)',
                          '0 0 20px rgba(239, 68, 68, 0.5)',
                        ]
                      }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      {isLoading ? (
                        <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span className="text-white font-bold text-lg tracking-wider">STOP</span>
                      )}
                    </motion.div>
                  </motion.button>
                )}
              </div>

              {/* Instructions */}
              <div className="text-center">
                {!isArmed ? (
                  <p className="text-gray-500 text-sm">
                    Click the button to arm the emergency stop
                  </p>
                ) : (
                  <motion.p
                    className="text-red-400 text-sm font-medium"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    System armed. Press STOP to confirm.
                  </motion.p>
                )}
              </div>

              {/* Warning Box */}
              <div className="bg-red-950/50 border border-red-900/50 rounded-lg p-4 space-y-2">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-gray-400 space-y-1">
                    <p><strong className="text-red-400">Warning:</strong> This action will:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                      <li>Block ALL incoming webhook trades</li>
                      <li>Affect ALL algorithm configurations</li>
                      <li>Remain active until midnight PT or manual reset</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Cancel Button */}
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

